// The owners' daily summary (N-27; ADR-0044; spec/16 §8, spec/17 §4.5; F-ANL-07):
//
//   GET  /v1/organizations/{o}/reports/daily-summary?day&venueId   reports:financial, owners only   one business day
//   POST /v1/organizations/{o}/reports/daily-summary/send          reports:financial, owners only   email it to yourself
//
// **What it is.** One business day's financial KPIs, for the organisation (every live venue, each on its own business
// day, totalled, with a row per venue) or for one venue: takings, play, food and drink, wallets and credit, cash, and
// players, each money figure a `Money` pair from the same ledger cells and the same domain definitions as the day close
// and the Reports tabs (spec/17 §1.1) — nothing here is computed a second way. Beside them, the **highlights**: each
// headline figure against the same weekday a week before and against the average of the seven business days before,
// the best venue, and flags for what needs an owner's eye.
//
// **Owners only** (ADR-0044). The routes declare `reports:financial`, and the server also requires the caller's role
// at the organisation to be `owner` — a manager holding `reports:financial` is refused `403`. No permission is added or
// widened. The summary that went out by email is kept, so its link lands on the same numbers (`sentAt`); a day whose
// summary did not go out is read live.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { OrganizationIdSchema, ShiftIdSchema, VenueIdSchema } from "../ids/schema.js";
import { commonProblemResponses, JSON_CONTENT_TYPE, jsonResponse, problemResponse } from "../openapi/responses.js";
import { REPORTS_FINANCIAL } from "../permissions/index.js";
import { MoneySchema } from "../primitives/money.js";
import { IsoDateTimeSchema, LocalDateSchema } from "../primitives/scalars.js";
import { OrganizationScopeParamsSchema } from "../primitives/scope.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { MetricTenderAmountSchema } from "./reports.js";
/** At most this many items in a summary's top food, drink and retail list. */
export const DAILY_SUMMARY_TOP_ITEMS = 3;
/** How a figure moved against a base: up, down or flat, by how much (basis points of the base). */
export const DailySummaryChangeSchema = z
    .object({
    direction: z.enum(['up', 'down', 'flat']),
    /** |current − base| ÷ |base| in basis points (10 000 = 100 %), half up; null when the base is zero. */
    basisPoints: z.int().min(0).nullable(),
})
    .openapi('DailySummaryChange', { description: 'A figure against a base: its direction and the change in basis points of the base.' });
export const DAILY_SUMMARY_MONEY_METRICS = ['netTakings', 'moneyIn', 'foodSales', 'refunds'];
export const DailySummaryMoneyTrendSchema = z
    .object({
    metric: z.enum(DAILY_SUMMARY_MONEY_METRICS),
    current: MoneySchema,
    /** The same weekday a week before (business day − 7); null with nothing recorded then. */
    lastWeek: MoneySchema.nullable(),
    /** The average of the seven business days before, half up to the minor unit; null with nothing recorded then. */
    average: MoneySchema.nullable(),
    vsLastWeek: DailySummaryChangeSchema.nullable(),
    vsAverage: DailySummaryChangeSchema.nullable(),
})
    .openapi('DailySummaryMoneyTrend', { description: 'One money figure against the same weekday last week and the trailing seven-day average.' });
export const DAILY_SUMMARY_COUNT_METRICS = ['sessions', 'playSeconds', 'utilisation', 'uniquePlayers', 'newPlayers'];
export const DailySummaryCountTrendSchema = z
    .object({
    metric: z.enum(DAILY_SUMMARY_COUNT_METRICS),
    /** `count`, `seconds` (play time) or `basis_points` (utilisation, 10 000 = 100 %). */
    unit: z.enum(['count', 'seconds', 'basis_points']),
    current: z.int().min(0).nullable(),
    lastWeek: z.int().min(0).nullable(),
    average: z.int().min(0).nullable(),
    vsLastWeek: DailySummaryChangeSchema.nullable(),
    vsAverage: DailySummaryChangeSchema.nullable(),
})
    .openapi('DailySummaryCountTrend', { description: 'One count, duration or ratio against the same weekday last week and the trailing seven-day average.' });
export const DailySummaryTakingsSchema = z
    .object({
    /** Sales before discounts, ex-tax: the day close's "sales". */
    grossSales: MoneySchema,
    discounts: MoneySchema,
    /** Money given back by refunds. */
    refunds: MoneySchema,
    refundCount: z.int().min(0),
    /** GST collected (M-03); zero where no sale was taxed. */
    tax: MoneySchema,
    /** M-01: what the business took — wallet spends count when spent, top-ups and bonus do not. */
    netTakings: MoneySchema,
    /** Cash, card, UPI and online taken, wallet top-ups included, less money handed back. */
    moneyIn: MoneySchema,
    /** Net takings by how they were paid (cash, counter card, counter UPI, online, wallet). */
    byTender: z.array(MetricTenderAmountSchema),
    /** Sold on account and not paid that day. */
    onAccount: MoneySchema,
})
    .openapi('DailySummaryTakings', { description: 'A business day’s takings, from the ledger.' });
export const DailySummaryPlaySchema = z
    .object({
    sessions: z.int().min(0),
    playSeconds: z.int().min(0),
    averageSessionSeconds: z.int().min(0).nullable(),
    /** Station-seconds in a session during open hours, and station-seconds open (M-10's parts). */
    inSessionSeconds: z.int().min(0),
    availableSeconds: z.int().min(0),
    /** M-10: in session ÷ open, basis points; null with no station-time open. */
    utilisationBasisPoints: z.int().min(0).nullable(),
    /** Stations offered (not disabled). */
    stations: z.int().min(0),
    /** Net takings ÷ stations offered, half up; null with none. */
    revenuePerStation: MoneySchema.nullable(),
    /** Play sold as passes, ex-tax. */
    passSales: MoneySchema,
    /** Play sold as time at the rate card (pay as you go, from wallets, on account), ex-tax. */
    payAsYouGo: MoneySchema,
})
    .openapi('DailySummaryPlay', { description: 'A business day’s play: sessions, hours, utilisation, passes against pay as you go.' });
export const DailySummaryItemSchema = z
    .object({ name: z.string(), kind: z.enum(['food', 'retail', 'other']), quantity: z.int().min(0), net: MoneySchema })
    .openapi('DailySummaryItem', { description: 'One of the day’s best-selling items: quantity and net sales (ex-tax, after line discounts).' });
export const DailySummaryFoodSchema = z
    .object({
    /** M-02 net sales of food and drink, and of retail, ex-tax. */
    foodSales: MoneySchema,
    retailSales: MoneySchema,
    /** Paid sales with food, drink or retail on them. */
    orders: z.int().min(0),
    topItems: z.array(DailySummaryItemSchema).max(DAILY_SUMMARY_TOP_ITEMS),
    /** Stock items low, below zero or sold out for stock when the summary was made; null where stock is not tracked. */
    stockAlerts: z.int().min(0).nullable(),
})
    .openapi('DailySummaryFood', { description: 'A business day’s food, drink and retail.' });
export const DailySummaryWalletSchema = z
    .object({
    topUps: MoneySchema,
    /** Bonus credit given with top-ups, coupons and manual credits: promotional, not money. */
    bonusIssued: MoneySchema,
    /** Wallet cash spent: revenue. */
    spent: MoneySchema,
    /** What players owe on account across the organisation when the summary was made (M-12); null with no facts. */
    onAccountOwed: MoneySchema.nullable(),
})
    .openapi('DailySummaryWallet', { description: 'A business day’s wallet flows, and what is owed on account.' });
export const DailySummaryDrawerSchema = z
    .object({
    shiftId: ShiftIdSchema,
    counterName: z.string().nullable(),
    status: z.enum(['open', 'closed']),
    expected: MoneySchema.nullable(),
    counted: MoneySchema.nullable(),
    /** Counted − expected; null while the drawer is open. */
    variance: MoneySchema.nullable(),
})
    .openapi('DailySummaryDrawer', { description: 'One drawer (shift) of the day: expected, counted and the variance.' });
export const DailySummaryCashSchema = z
    .object({
    /** Over the drawers counted on the day. */
    expected: MoneySchema.nullable(),
    counted: MoneySchema.nullable(),
    variance: MoneySchema,
    drawers: z.array(DailySummaryDrawerSchema),
    /** Shifts of the day still open when the summary was made. */
    openShifts: z.int().min(0),
})
    .openapi('DailySummaryCash', { description: 'A business day’s cash: expected against counted, and the variance per drawer.' });
export const DailySummaryPlayersSchema = z
    .object({
    /** M-06: players with a session that day (a player counted once across venues). */
    unique: z.int().min(0),
    /** M-07: of them, first time at the organisation. */
    new: z.int().min(0),
})
    .openapi('DailySummaryPlayers', { description: 'Players that day: how many, and how many were new. Counts only.' });
export const DailySummaryKpisSchema = z
    .object({
    takings: DailySummaryTakingsSchema,
    play: DailySummaryPlaySchema,
    food: DailySummaryFoodSchema,
    wallet: DailySummaryWalletSchema,
    cash: DailySummaryCashSchema,
    players: DailySummaryPlayersSchema,
})
    .openapi('DailySummaryKpis', { description: 'The KPIs of one business day, for a venue or the organisation’s venues summed.' });
export const DAILY_SUMMARY_FLAG_KINDS = ['day_not_closed', 'cash_variance', 'refunds_high', 'shift_open', 'station_offline', 'stock_alerts'];
export const DailySummaryFlagSchema = z
    .object({
    kind: z.enum(DAILY_SUMMARY_FLAG_KINDS),
    venueId: VenueIdSchema,
    venueName: z.string(),
    /** The money the flag is about (the cash variance, the refunds); null otherwise. */
    amount: MoneySchema.nullable(),
    /** How many (shifts open, stations, stock items); null otherwise. */
    count: z.int().min(0).nullable(),
})
    .openapi('DailySummaryFlag', {
    description: 'Something an owner should look at: the day not closed yet, a cash variance over the alert amount, refunds well above ' +
        'usual, a shift still open, a station that was offline a long time during a session, stock alerts.',
});
export const DailySummaryVenueSchema = z
    .object({
    venueId: VenueIdSchema,
    name: z.string(),
    /** The venue’s business day the figures are for (its timezone and cutoff). */
    businessDay: LocalDateSchema,
    timeZone: z.string(),
    currency: z.string().length(3),
    /** Whether the day was closed (by staff or at the rollover) when the summary was made. */
    dayClosed: z.boolean(),
    /** How it was closed — by staff, or at the automatic rollover — when it was; absent while open. */
    closeMode: z.enum(['manual', 'auto']).optional(),
    closedAt: IsoDateTimeSchema.nullable(),
    kpis: DailySummaryKpisSchema,
    moneyTrends: z.array(DailySummaryMoneyTrendSchema),
    trends: z.array(DailySummaryCountTrendSchema),
})
    .openapi('DailySummaryVenue', { description: 'One venue’s business day in a daily summary.' });
export const DailySummarySchema = z
    .object({
    /** The organisation’s summary (every live venue) or one venue’s. */
    kind: z.enum(['organization', 'venue']),
    organizationId: OrganizationIdSchema,
    organizationName: z.string(),
    /** The business date; each venue’s own business day of that date, in its own timezone. */
    businessDay: LocalDateSchema,
    /** The currency the totals are in; null when the venues trade in more than one (then there are no totals). */
    currency: z.string().length(3).nullable(),
    /** The venues summed (a venue summary: that venue); null when currencies differ. */
    totals: DailySummaryKpisSchema.nullable(),
    moneyTrends: z.array(DailySummaryMoneyTrendSchema),
    trends: z.array(DailySummaryCountTrendSchema),
    /** Every venue in the summary, most net takings first. */
    venues: z.array(DailySummaryVenueSchema),
    /** The organisation summary’s best venue by net takings; null for a venue summary or with no takings. */
    bestVenueId: VenueIdSchema.nullable(),
    flags: z.array(DailySummaryFlagSchema),
    /** When the figures were read. */
    generatedAt: IsoDateTimeSchema,
    /** Why it was made: every venue’s day closed, the deadline passed with a day still open, or read on request. */
    trigger: z.enum(['closed', 'deadline', 'on_request']),
    /** When it went out to the organisation’s owners; null for a summary read live. */
    sentAt: IsoDateTimeSchema.nullable(),
    /** True when a read reached its limit and a figure may be low. */
    partial: z.boolean(),
})
    .openapi('DailySummary', {
    description: 'The owners’ end-of-day summary (N-27): one business day’s KPIs for the organisation or a venue, with comparisons, ' +
        'the best venue and flags. Counts and items only — no player is named.',
});
export const DailySummaryQuerySchema = z
    .object({
    /** The business date; without it, the latest business day that has ended at every venue in the summary. */
    day: LocalDateSchema.optional(),
    /** One venue’s summary; without it, the organisation’s. */
    venueId: VenueIdSchema.optional(),
})
    .strict()
    .openapi('DailySummaryQuery');
export const SendDailySummarySchema = z
    .object({
    day: LocalDateSchema.optional(),
    venueId: VenueIdSchema.optional(),
})
    .strict()
    .openapi('SendDailySummary', { description: 'Which summary to email you now: a business date and a venue, or the organisation’s latest.' });
export const DailySummarySentSchema = z
    .object({
    businessDay: LocalDateSchema,
    kind: z.enum(['organization', 'venue']),
    venueId: VenueIdSchema.nullable(),
})
    .openapi('DailySummarySent', { description: 'The summary that is on its way to your email address and your inbox.' });
const OWNERS_ONLY = 'Requires `reports:financial` and the owner role at the organisation: a manager is refused.';
export const getDailySummaryRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/reports/daily-summary',
    operationId: 'getDailySummary',
    tags: ['reports'],
    summary: 'The owners’ daily summary for one business day',
    description: 'One business day’s KPIs — takings, play, food and drink, wallets and credit, cash, players — for the organisation ' +
        '(every live venue on its own business day, totalled) or one venue, against the same weekday last week and the ' +
        'trailing seven-day average, with flags. The summary that was emailed is returned as it was sent; otherwise it is ' +
        `read live. ${OWNERS_ONLY}`,
    [PERMISSION_EXTENSION]: REPORTS_FINANCIAL,
    request: { params: OrganizationScopeParamsSchema, query: DailySummaryQuerySchema },
    responses: {
        200: jsonResponse(DailySummarySchema, 'The summary.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: problemResponse('Only an owner of the organisation reads the daily summary.', 'forbidden', 'second-factor-required'),
        404: problemResponse('No such venue in this organisation, or no live venue.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const sendDailySummaryRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/reports/daily-summary/send',
    operationId: 'sendDailySummary',
    tags: ['reports'],
    summary: 'Email the daily summary to yourself now',
    description: 'Sends one business day’s summary — by default the organisation’s latest — to the caller’s own verified email ' +
        'address and inbox, and to nobody else. It goes through the delivery pipeline, so it is retried and recorded like ' +
        `every notification. ${OWNERS_ONLY}`,
    [PERMISSION_EXTENSION]: REPORTS_FINANCIAL,
    request: {
        params: OrganizationScopeParamsSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: SendDailySummarySchema } }, required: true },
    },
    responses: {
        202: jsonResponse(DailySummarySentSchema, 'On its way.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: problemResponse('Only an owner of the organisation gets the daily summary, and only at a verified email address.', 'forbidden', 'second-factor-required', 'email-not-verified'),
        404: problemResponse('No such venue in this organisation, or no live venue.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=daily-summary.js.map